import React, { useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Business as BusinessIcon,
  Book as BookIcon,
  MenuBook as MenuBookIcon,
  List as ListIcon,
  Person as PersonIcon,
  CloudUpload as CloudUploadIcon,
  Webhook as WebhookIcon,
  SyncAlt as SyncAltIcon,
  AccountCircle,
  Logout,
  Inventory2 as ProductIcon,
  LocalOffer as ComboIcon,
  NotificationsActive as NotificationsActiveIcon,
  PendingActions as PendingActionsIcon,
  BarChart as BarChartIcon,
  Assessment as AssessmentIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import Badge from '@mui/material/Badge';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AdminNotificationsProvider, useAdminNotificationsContext } from '../contexts/AdminNotificationsContext';
import { AdminModuleId } from '../services/firebaseFunctions';
import DevModeEntitlementsDialog from './DevModeEntitlementsDialog';

const drawerWidth = 240;
const isStagingProject = process.env.REACT_APP_FIREBASE_PROJECT_ID === 'reedy-staging-b4ef1';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavMenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  /** Módulo RBAC que controla a visibilidade deste item (REEDY-17). */
  module: AdminModuleId;
}

// menuItems — cada entrada só é exibida a quem tem, no mínimo, permissão de 'ver' no módulo
// correspondente (`.agents/memory/contracts.md` §"Controle de Acesso Administrativo (RBAC)").
// "Administradores" é o módulo novo desta entrega; os demais 15 preservam o menu existente.
const menuItems: NavMenuItem[] = [
  { text: 'Analytics', icon: <BarChartIcon />, path: '/analytics', module: 'analytics' },
  { text: 'Empresas', icon: <BusinessIcon />, path: '/companies', module: 'empresas' },
  { text: 'Grupos', icon: <BusinessIcon />, path: '/company-groups', module: 'empresas' },
  { text: 'Redes', icon: <BusinessIcon />, path: '/networks', module: 'redes' },
  { text: 'Títulos', icon: <BookIcon />, path: '/titles', module: 'titulos' },
  { text: 'Editoras', icon: <MenuBookIcon />, path: '/publishers', module: 'editoras' },
  { text: 'Listas', icon: <ListIcon />, path: '/lists', module: 'listas' },
  { text: 'Ativos', icon: <ProductIcon />, path: '/products', module: 'ativos' },
  { text: 'Combos', icon: <ComboIcon />, path: '/combos', module: 'combos' },
  { text: 'Upload', icon: <CloudUploadIcon />, path: '/upload', module: 'upload' },
  { text: 'Usuários', icon: <PersonIcon />, path: '/users', module: 'usuarios' },
  { text: 'Webhooks EVO', icon: <WebhookIcon />, path: '/evo-webhooks', module: 'webhooks_evo' },
  { text: 'Conferência EVO', icon: <SyncAltIcon />, path: '/evo-conference', module: 'conferencia_evo' },
  { text: 'Notificações', icon: <NotificationsActiveIcon />, path: '/admin-notifications', module: 'notificacoes' },
  { text: 'Entregas Pendentes', icon: <PendingActionsIcon />, path: '/delivery-queue', module: 'entregas_pendentes' },
  { text: 'Relatórios', icon: <AssessmentIcon />, path: '/reports', module: 'relatorios' },
  { text: 'Administradores', icon: <AdminPanelSettingsIcon />, path: '/admins', module: 'administradores' },
];

// Rota "ativa" de um item do menu: caminho exato, exceto Editoras (qualquer `/publishers/...`) e
// Webhooks EVO (`/evo-webhooks` e rotas filhas: detalhe, backfill e conferência — REEDY-114).
const isMenuItemActive = (item: NavMenuItem, pathname: string): boolean => {
  if (item.path === '/publishers') return pathname.startsWith('/publishers');
  if (item.path === '/evo-webhooks') return pathname === item.path || pathname.startsWith(`${item.path}/`);
  return pathname === item.path;
};

const LayoutContent: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [devModeOpen, setDevModeOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, hasPermission, refreshAdminAccess } = useAuth();
  const { notifications } = useAdminNotificationsContext();
  const notificationCount = notifications.length;

  // Só exibe o item de menu de módulos para os quais o administrador logado tem, no mínimo,
  // permissão de 'ver' (REEDY-17). A checagem no backend/rules é a fronteira real; isto é
  // conselho de navegação, não a barreira de segurança (`contracts.md` Invariante 2).
  const visibleMenuItems = menuItems.filter((item) => hasPermission(item.module, 'ver'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  const handleRefreshPermissions = () => {
    void refreshAdminAccess();
    handleMenuClose();
  };

  const drawer = (
    <Box>
      <Toolbar>
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600 }}>
          Reedy Admin
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {visibleMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={isMenuItemActive(item, location.pathname)}
              onClick={() => {
                navigate(item.path);
                if (isMobile) {
                  setMobileOpen(false);
                }
              }}
              sx={{
                '&.Mui-selected': {
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                  '&:hover': {
                    bgcolor: 'primary.main',
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: isMenuItemActive(item, location.pathname) ? 'inherit' : 'text.primary',
                }}
              >
                {item.path === '/admin-notifications' && notificationCount > 0 ? (
                  <Badge badgeContent={notificationCount} color="error" max={99}>
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { md: 'none' } }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find((item) => isMenuItemActive(item, location.pathname))?.text || 'Empresas'}
          </Typography>
          <IconButton
            color="inherit"
            aria-label="notificações"
            onClick={() => navigate('/admin-notifications')}
          >
            <Badge badgeContent={notificationCount > 0 ? notificationCount : undefined} color="error" max={99}>
              <NotificationsActiveIcon />
            </Badge>
          </IconButton>
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenuOpen}
            color="inherit"
          >
            <AccountCircle />
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem disabled>
              <Typography variant="body2">{user}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleRefreshPermissions}>
              <ListItemIcon>
                <SyncIcon fontSize="small" />
              </ListItemIcon>
              Atualizar permissões
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Sair
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }} aria-label="mailbox folders">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
        }}
      >
        {children}
      </Box>
      {isStagingProject && (
        <>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setDevModeOpen(true)}
            sx={{
              position: 'fixed',
              top: { xs: 70, md: 76 },
              right: { xs: 12, md: 24 },
              zIndex: (theme) => theme.zIndex.appBar + 1,
              borderRadius: 20,
              textTransform: 'none',
              boxShadow: 4,
            }}
          >
            Dev Mode
          </Button>
          <DevModeEntitlementsDialog open={devModeOpen} onClose={() => setDevModeOpen(false)} />
        </>
      )}
    </Box>
  );
};

const Layout: React.FC<LayoutProps> = ({ children }) => (
  <AdminNotificationsProvider>
    <LayoutContent>{children}</LayoutContent>
  </AdminNotificationsProvider>
);

export default Layout;
